import React, { useState } from 'react';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from './firebase';
import { formatDateTime, getUserNameByEmail, formatDisplayNumber } from './utils/helpers';
import './BoardView.css';

const STATUS_ORDER = ['Open', 'In Progress', 'Resolved', 'Closed', 'Reopen', 'Recommend Cancel', 'Cancelled'];

const PRIORITY_ORDER = { 'Critical': 4, 'High': 3, 'Medium': 2, 'Low': 1 };

function BoardView({ 
  defects, 
  user, 
  userRole, 
  users,
  onEditDefect,
  onViewDefect,
  onCancelDefect, 
  onAddComment, 
  commentInputs, 
  setCommentInputs,
  onCreateNotification,
  editingComment,
  editCommentText,
  setEditCommentText,
  onEditComment,
  onSaveCommentEdit,
  onCancelCommentEdit,
  onConvertToFeature,
  onHiddenLanesChange
}) {
  const [hiddenLanes, setHiddenLanes] = useState(new Set());
  const [collapsedLanes, setCollapsedLanes] = useState(new Set());
  const [draggingDefect, setDraggingDefect] = useState(null);
  const [laneSorts, setLaneSorts] = useState({}); // { status: { field: 'priority', direction: 'desc' } }
  const [expandedDefects, setExpandedDefects] = useState(new Set());

  const isAdmin = userRole === 'Admin';
  const isTester = userRole === 'Tester/QA';

  const toggleExpanded = (defectId) => {
    setExpandedDefects(prev => {
      const newSet = new Set(prev);
      if (newSet.has(defectId)) {
        newSet.delete(defectId);
      } else {
        newSet.add(defectId);
      }
      return newSet;
    });
  };

  const toggleLane = (status) => {
    setHiddenLanes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(status)) {
        newSet.delete(status);
      } else {
        newSet.add(status);
      }
      // Notify parent component of hidden lanes change
      if (onHiddenLanesChange) {
        onHiddenLanesChange(newSet);
      }
      return newSet;
    });
  };

  const toggleCollapsed = (status) => {
    setCollapsedLanes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(status)) {
        newSet.delete(status);
      } else {
        newSet.add(status);
      }
      return newSet;
    });
  };

  const handleSort = (status, field) => {
    setLaneSorts(prev => {
      const current = prev[status];
      let newDirection = 'desc';
      
      if (current && current.field === field) {
        newDirection = current.direction === 'desc' ? 'asc' : 'desc';
      }
      
      return {
        ...prev,
        [status]: { field, direction: newDirection }
      };
    });
  };

  const sortDefects = (defectsToSort, status) => {
    const sortConfig = laneSorts[status] || { field: 'priority', direction: 'desc' };
    
    return [...defectsToSort].sort((a, b) => {
      let aVal, bVal;
      
      if (sortConfig.field === 'priority') {
        aVal = PRIORITY_ORDER[a.priority] || 0;
        bVal = PRIORITY_ORDER[b.priority] || 0;
      } else if (sortConfig.field === 'createdAt') {
        aVal = a.createdAt?.toMillis() || 0;
        bVal = b.createdAt?.toMillis() || 0;
      } else if (sortConfig.field === 'updatedAt') {
        aVal = a.updatedAt?.toMillis() || 0;
        bVal = b.updatedAt?.toMillis() || 0;
      } else if (sortConfig.field === 'applicationName') {
        aVal = (a.applicationName || '').toLowerCase();
        bVal = (b.applicationName || '').toLowerCase();
        // String comparison
        if (sortConfig.direction === 'desc') {
          return bVal.localeCompare(aVal);
        } else {
          return aVal.localeCompare(bVal);
        }
      }
      
      if (sortConfig.direction === 'desc') {
        return bVal - aVal;
      } else {
        return aVal - bVal;
      }
    });
  };

  const handleDragStart = (e, defect) => {
    if (!isAdmin && !isTester) {
      e.preventDefault();
      return;
    }
    setDraggingDefect(defect);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, newStatus) => {
    e.preventDefault();
    if (!draggingDefect) return;

    if (draggingDefect.status === newStatus) {
      setDraggingDefect(null);
      return;
    }

    if (isTester && newStatus === 'Cancelled') {
      alert('Only Admins can set status to Cancelled.');
      setDraggingDefect(null);
      return;
    }

    const oldStatus = draggingDefect.status;

    try {
      await updateDoc(doc(db, 'defects', draggingDefect.id), {
        status: newStatus,
        updatedAt: serverTimestamp(),
        updatedBy: user.email
      });

      // Create notification for status change
      if (newStatus === 'Resolved') {
        onCreateNotification({
          userId: draggingDefect.createdBy,
          type: 'status_resolved',
          message: `Defect "${draggingDefect.title}" was marked as Resolved`,
          defectId: draggingDefect.id,
          defectTitle: draggingDefect.title
        });
      }

      // Notify anyone assigned
      [draggingDefect.assignedToFix, draggingDefect.assignedToTest].forEach(assignedEmail => {
        if (assignedEmail) {
          const assignedUser = users.find(u => u.email === assignedEmail);
          if (assignedUser) {
            onCreateNotification({
              userId: assignedUser.id,
              type: 'defect_updated',
              message: `Status changed from "${oldStatus}" to "${newStatus}" on defect "${draggingDefect.title}"`,
              defectId: draggingDefect.id,
              defectTitle: draggingDefect.title
            });
          }
        }
      });

      // Notify creator
      if (draggingDefect.createdBy !== user.uid) {
        onCreateNotification({
          userId: draggingDefect.createdBy,
          type: 'defect_updated',
          message: `Status changed to "${newStatus}" on your defect "${draggingDefect.title}"`,
          defectId: draggingDefect.id,
          defectTitle: draggingDefect.title
        });
      }
    } catch (err) {
      alert('Error updating defect status.');
    }

    setDraggingDefect(null);
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getStatusColor = (status) => ({
    'Open': '#2563eb',
    'In Progress': '#7c3aed',
    'Resolved': '#16a34a',
    'Closed': '#6b7280',
    'Reopen': '#dc2626',
    'Cancelled': '#374151',
    'Recommend Cancel': '#f59e0b'
  }[status] || '#6b7280');

  const getPriorityColor = (priority) => ({
    'Critical': '#dc2626',
    'High': '#ea580c',
    'Medium': '#ca8a04',
    'Low': '#16a34a'
  }[priority] || '#6b7280');

  const getUserNameByEmail = (email) => {
    if (!email) return null;
    const user = users.find(u => u.email === email);
    return user ? user.email.split('@')[0] : email.split('@')[0];
  };

  // Group defects by status
  const defectsByStatus = STATUS_ORDER.reduce((acc, status) => {
    const statusDefects = defects.filter(d => d.status === status);
    acc[status] = sortDefects(statusDefects, status);
    return acc;
  }, {});

  const getSortIcon = (status, field) => {
    const sortConfig = laneSorts[status];
    if (!sortConfig || sortConfig.field !== field) return '⇅';
    return sortConfig.direction === 'desc' ? '▼' : '▲';
  };

  return (
    <div className="board-view">
      {/* Lane Visibility Controls */}
      <div className="lane-visibility-controls">
        <span className="controls-label">Show/Hide Lanes:</span>
        {STATUS_ORDER.map(status => (
          <button
            key={status}
            className={`lane-visibility-btn ${hiddenLanes.has(status) ? 'lane-visibility-btn--hidden' : ''}`}
            onClick={() => toggleLane(status)}
            style={{ backgroundColor: getStatusColor(status) }}
          >
            {hiddenLanes.has(status) ? '👁️' : '👁️‍🗨️'} {status}
          </button>
        ))}
      </div>

      <div className="board-wrapper">
        <div className="board-container">
          {STATUS_ORDER.map(status => {
          const statusDefects = defectsByStatus[status] || [];
          const isHidden = hiddenLanes.has(status);
          const isRecommendCancel = status === 'Recommend Cancel';

          // Skip rendering if hidden
          if (isHidden) return null;

          return (
            <div
              key={status}
              className={`swimlane${isRecommendCancel && statusDefects.length > 0 ? ' swimlane--alert' : ''}`}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, status)}
            >
              <div
                className="swimlane-header"
                style={{ backgroundColor: getStatusColor(status) }}
              >
                <div className="swimlane-title">
                  <span>{status}</span>
                  <span className="swimlane-count">{statusDefects.length}</span>
                </div>
              </div>

              <div className="swimlane-sort-controls">
                <button 
                  className="sort-btn" 
                  onClick={() => handleSort(status, 'priority')}
                  title="Sort by Priority"
                >
                  Priority {getSortIcon(status, 'priority')}
                </button>
                <button 
                  className="sort-btn" 
                  onClick={() => handleSort(status, 'createdAt')}
                  title="Sort by Created"
                >
                  Created {getSortIcon(status, 'createdAt')}
                </button>
                <button 
                  className="sort-btn" 
                  onClick={() => handleSort(status, 'updatedAt')}
                  title="Sort by Updated"
                >
                  Updated {getSortIcon(status, 'updatedAt')}
                </button>
                <button 
                  className="sort-btn" 
                  onClick={() => handleSort(status, 'applicationName')}
                  title="Sort by Application"
                >
                  Application {getSortIcon(status, 'applicationName')}
                </button>
              </div>

                  <div className="swimlane-content">
                    {statusDefects.length === 0 ? (
                      <div className="swimlane-empty">No defects</div>
                    ) : (
                      statusDefects.map(defect => {
                        const canEdit = isAdmin || isTester;
                        const canCancel = isAdmin;
                        const isDragging = draggingDefect?.id === defect.id;

                        return (
                          <div
                            key={defect.id}
                            className={`board-card${isDragging ? ' board-card--dragging' : ''}`}
                            draggable={canEdit}
                            onDragStart={(e) => handleDragStart(e, defect)}
                          >
                            <div className="board-card-header">
                              <div className="board-card-id-group">
                                {typeof defect.displayNumber === 'number' && (
                                  <div className="board-card-seq" title="Sequential number for this application">
                                    {formatDisplayNumber(defect, 'D')}
                                  </div>
                                )}
                                <div className="board-card-id" title="Internal ID">
                                  {defect.id.substring(0, 8)}
                                </div>
                              </div>
                              <span
                                className="board-card-priority"
                                style={{ backgroundColor: getPriorityColor(defect.priority) }}
                              >
                                {defect.priority}
                              </span>
                            </div>

                            {defect.applicationName && (
                              <div className="board-card-app">
                                <span className="app-icon">📱</span> {defect.applicationName}
                              </div>
                            )}

                            <h4 className="board-card-title">{defect.title}</h4>

                            <div className="board-card-dates">
                              <div className="board-card-date">
                                <strong>Created:</strong> {formatDateTime(defect.createdAt)}
                              </div>
                              <div className="board-card-date">
                                <strong>Updated:</strong> {formatDateTime(defect.updatedAt)}
                              </div>
                            </div>

                            <div className="board-card-meta">
                              <span>By: {defect.createdByEmail || 'Unknown'}</span>
                            </div>

                            {(defect.assignedToFix || defect.assignedToTest) && (
                              <div className="board-card-assignments">
                                {defect.assignedToFix && (
                                  <div className="assignment-badge assignment-badge--fix">
                                    🔧 Fix: {getUserNameByEmail(defect.assignedToFix)}
                                  </div>
                                )}
                                {defect.assignedToTest && (
                                  <div className="assignment-badge assignment-badge--test">
                                    🧪 Test: {getUserNameByEmail(defect.assignedToTest)}
                                  </div>
                                )}
                              </div>
                            )}

                            <div className="board-card-actions">
                              <button 
                                className="btn btn-small btn-secondary"
                                onClick={() => toggleExpanded(defect.id)}
                              >
                                {expandedDefects.has(defect.id) ? 'Hide' : 'Show'} Comments ({Array.isArray(defect.comments) ? defect.comments.length : 0})
                              </button>
                              <button className="btn btn-small btn-view" onClick={() => onViewDefect(defect)}>
                                👁️ View
                              </button>
                              {canEdit && defect.status !== 'Cancelled' && (
                                <button className="btn btn-small btn-edit" onClick={() => onEditDefect(defect)}>
                                  Edit
                                </button>
                              )}
                              {canCancel && defect.status !== 'Cancelled' && (
                                <button className="btn btn-small btn-cancel" onClick={() => onCancelDefect(defect)}>
                                  Cancel
                                </button>
                              )}
                              {isAdmin && (
                                <button 
                                  className="btn btn-small btn-secondary"
                                  onClick={() => onConvertToFeature(defect)}
                                  title="Convert this defect to a feature request"
                                >
                                  🔄 To Feature
                                </button>
                              )}
                            </div>

                            {expandedDefects.has(defect.id) && (
                              <div className="defect-comments-section">
                                <div className="comments-list">
                                  {(!defect.comments || defect.comments.length === 0) ? (
                                    <p className="no-comments">No comments yet</p>
                                  ) : (
                                    defect.comments.map((comment, idx) => {
                                      const isEditing = editingComment?.defectId === defect.id && editingComment?.commentIndex === idx;
                                      const canEditComment = user.email === comment.createdBy || isAdmin || isTester;
                                      
                                      return (
                                        <div key={idx} className="comment">
                                          <div className="comment-header">
                                            <strong>{getUserNameByEmail(comment.createdBy)}</strong>
                                            <span className="comment-date">
                                              {formatDateTime(comment.createdAt)}
                                              {comment.editedAt && ' (edited)'}
                                            </span>
                                          </div>
                                          
                                          {isEditing ? (
                                            <div className="comment-edit-mode">
                                              <textarea
                                                value={editCommentText}
                                                onChange={e => setEditCommentText(e.target.value)}
                                                className="comment-edit-input"
                                              />
                                              <div className="comment-edit-actions">
                                                <button 
                                                  className="btn btn-small btn-primary"
                                                  onClick={() => onSaveCommentEdit(defect.id, idx)}
                                                >
                                                  Save
                                                </button>
                                                <button 
                                                  className="btn btn-small btn-secondary"
                                                  onClick={onCancelCommentEdit}
                                                >
                                                  Cancel
                                                </button>
                                              </div>
                                            </div>
                                          ) : (
                                            <>
                                              <p className="comment-text">{comment.text}</p>
                                              {canEditComment && (
                                                <div className="comment-actions">
                                                  <button
                                                    className="btn-link btn-link--small"
                                                    onClick={() => onEditComment(defect.id, idx, comment)}
                                                  >
                                                    Edit
                                                  </button>
                                                </div>
                                              )}
                                            </>
                                          )}
                                        </div>
                                      );
                                    })
                                  )}
                                </div>

                                <div className="comment-input-container">
                                  <input
                                    type="text"
                                    className="comment-input"
                                    placeholder="Add a comment..."
                                    value={commentInputs[defect.id] || ''}
                                    onChange={e => setCommentInputs(prev => ({
                                      ...prev,
                                      [defect.id]: e.target.value
                                    }))}
                                    onKeyPress={e => {
                                      if (e.key === 'Enter' && commentInputs[defect.id]?.trim()) {
                                        onAddComment(defect, commentInputs[defect.id]);
                                      }
                                    }}
                                  />
                                  <button
                                    className="btn btn-small btn-primary"
                                    onClick={() => onAddComment(defect, commentInputs[defect.id])}
                                    disabled={!commentInputs[defect.id]?.trim()}
                                  >
                                    Add
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
            </div>
          );
        })}
        </div>
      </div>
    </div>
  );
}

export default BoardView;
